//#region src/index.ts
const MAX_LOGS = 500;
/**
* Native reads `window.logs.join('\n')` when a WebView loading timeout fires
* and reports it as the `logs` custom key of `web_view_timeout_error`, where
* Crashlytics keeps only the first 1024 characters. The breadcrumb line is
* therefore always `window.logs[0]`, and capped so the errors after it still
* get most of that budget.
*/
const BREADCRUMB_PREFIX = "[bc]";
const MAX_BREADCRUMB_LINE = 280;
const MAX_DETAIL = 40;
const BREADCRUMB_OVERFLOW = "…";
const inBrowser = typeof window !== "undefined" && typeof window.addEventListener === "function" && typeof document !== "undefined";
if (inBrowser) window.logs = [];
let breadcrumbLine = "";
let breadcrumbFull = false;
const ms = (at) => Math.round(at);
/**
* Appends one `code:time` token to the boot breadcrumb line, where time is ms
* since navigation start (`performance.now()`) and detail, when given,
* follows after a slash. The line stops growing at its cap and marks the cut.
*
* For a handful of boot milestones only — it is not a log. Errors still go
* through `console.error`.
*/
function breadcrumb(code, detail) {
	if (!inBrowser || breadcrumbFull) return;
	const suffix = detail === void 0 ? "" : `/${String(detail).slice(0, MAX_DETAIL)}`;
	const base = breadcrumbLine || BREADCRUMB_PREFIX;
	let next = `${base} ${code}:${ms(performance.now())}${suffix}`;
	if (next.length > MAX_BREADCRUMB_LINE) {
		breadcrumbFull = true;
		next = `${base} ${BREADCRUMB_OVERFLOW}`;
	}
	writeBreadcrumbLine(next);
}
function writeBreadcrumbLine(next) {
	const logs = window.logs;
	const index = breadcrumbLine ? logs.indexOf(breadcrumbLine) : -1;
	breadcrumbLine = next;
	if (index === 0) {
		logs[0] = next;
		return;
	}
	if (index > 0) logs.splice(index, 1);
	logs.unshift(next);
}
function format(args) {
	return args.map((a) => {
		if (typeof a === "string") return a;
		if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack ?? ""}`;
		try {
			return JSON.stringify(a);
		} catch {
			return String(a);
		}
	}).join(" ");
}
function push(level, message) {
	const logs = window.logs;
	logs.push(`[${level}] ${message}`);
	if (logs.length > MAX_LOGS) logs.splice(breadcrumbLine && logs[0] === breadcrumbLine ? 1 : 0, 1);
}
if (inBrowser) installDiagnostics();
function installDiagnostics() {
	for (const level of ["error"]) {
		const original = console[level].bind(console);
		console[level] = (...args) => {
			push(level, format(args));
			original(...args);
		};
	}
	window.addEventListener("error", (event) => {
		push("uncaught", `${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`);
	});
	window.addEventListener("unhandledrejection", (event) => {
		const reason = event.reason;
		push("unhandledrejection", reason instanceof Error ? `${reason.name}: ${reason.message}\n${reason.stack ?? ""}` : format([reason]));
	});
	breadcrumb("b", document.visibilityState === "hidden" ? "h" : void 0);
	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => breadcrumb("dcl"), { once: true });
	else breadcrumb("dcl", "early");
	if (document.readyState === "complete") breadcrumb("ld", "early");
	else window.addEventListener("load", () => breadcrumb("ld"), { once: true });
	document.addEventListener("visibilitychange", () => {
		breadcrumb(document.visibilityState === "hidden" ? "hid" : "vis");
	});
	const GAP_TICK_MS = 1e3;
	const GAP_THRESHOLD_MS = 1500;
	const GAP_WATCH_UNTIL_MS = 3e4;
	function scheduleGapTick() {
		const expectedAt = performance.now() + GAP_TICK_MS;
		setTimeout(() => {
			const now = performance.now();
			const late = now - expectedAt;
			if (late > GAP_THRESHOLD_MS) breadcrumb("gap", ms(late));
			if (now < GAP_WATCH_UNTIL_MS) scheduleGapTick();
		}, GAP_TICK_MS);
	}
	scheduleGapTick();
}
//#endregion
export { breadcrumb };

//# sourceMappingURL=index.js.map